// Restored from production OTA 2026-08-21 (module 1414). The rate tables below
// were kept; roundFareForCash, TANZANIA_FEES, TANZANIA_ALLOWED_CAR_TYPES and
// getCountryCarTypes were lost and are still imported by TaxiModal,
// DriverTrips and CarEditScreen.
import { normalizeCountry, isTanzaniaCountry } from "./CountryFunctions";

export { isTanzaniaCountry };

const rateMapping = {
  burundi: "rate_per_unit_distance_burundi",
  canada: "rate_per_unit_distance_canada",
  "democratic republic of the congo": "rate_per_unit_distance_drc",
  kenya: "rate_per_unit_distance_kenya",
  "south africa": "rate_per_unit_distance_rsa",
  rwanda: "rate_per_unit_distance_rwanda",
  uganda: "rate_per_unit_distance_uganda",
  nigeria: "rate_per_unit_distance_nigeria",
  zambia: "rate_per_unit_distance_zambia",
  tanzania: "rate_per_unit_distance_tanzania",
  "united republic of tanzania": "rate_per_unit_distance_tanzania",
  benin: "rate_per_unit_distance_benin",
  "burkina faso": "rate_per_unit_distance_burkina_faso",
  cameroon: "rate_per_unit_distance_cameroon",
  gabon: "rate_per_unit_distance_gabon",
  ghana: "rate_per_unit_distance_ghana",
  lesotho: "rate_per_unit_distance_lesotho",
  malawi: "rate_per_unit_distance_malawi",
  mozambique: "rate_per_unit_distance_mozambique",
  senegal: "rate_per_unit_distance_senegal",
  "sierra leone": "rate_per_unit_distance_sierra_leone",
};

const ratePerHourMapping = {
  burundi: "rate_per_hour_burundi",
  canada: "rate_per_hour_canada",
  "democratic republic of the congo": "rate_per_hour_drc",
  kenya: "rate_per_hour_kenya",
  "south africa": "rate_per_hour_rsa",
  rwanda: "rate_per_hour_rwanda",
  uganda: "rate_per_hour_uganda",
  nigeria: "rate_per_hour_nigeria",
  zambia: "rate_per_hour_zambia",
  tanzania: "rate_per_hour_tanzania",
  "united republic of tanzania": "rate_per_hour_tanzania",
  benin: "rate_per_hour_benin",
  "burkina faso": "rate_per_hour_burkina_faso",
  cameroon: "rate_per_hour_cameroon",
  gabon: "rate_per_hour_gabon",
  ghana: "rate_per_hour_ghana",
  lesotho: "rate_per_hour_lesotho",
  malawi: "rate_per_hour_malawi",
  mozambique: "rate_per_hour_mozambique",
  senegal: "rate_per_hour_senegal",
  "sierra leone": "rate_per_hour_sierra_leone",
};

const baseFareMapping = {
  burundi: "base_fare_burundi",
  canada: "base_fare_canada",
  "democratic republic of the congo": "base_fare_drc",
  kenya: "base_fare_kenya",
  "south africa": "base_fare_rsa",
  rwanda: "base_fare_rwanda",
  uganda: "base_fare_uganda",
  nigeria: "base_fare_nigeria",
  zambia: "base_fare_zambia",
  tanzania: "base_fare_tanzania",
  "united republic of tanzania": "base_fare_tanzania",
  benin: "base_fare_benin",
  "burkina faso": "base_fare_burkina_faso",
  cameroon: "base_fare_cameroon",
  gabon: "base_fare_gabon",
  ghana: "base_fare_ghana",
  lesotho: "base_fare_lesotho",
  malawi: "base_fare_malawi",
  mozambique: "base_fare_mozambique",
  senegal: "base_fare_senegal",
  "sierra leone": "base_fare_sierra_leone",
};

const minFareMapping = {
  burundi: "min_fare_burundi",
  canada: "min_fare_canada",
  "democratic republic of the congo": "min_fare_drc",
  kenya: "min_fare_kenya",
  "south africa": "min_fare_rsa",
  rwanda: "min_fare_rwanda",
  uganda: "min_fare_uganda",
  nigeria: "min_fare_nigeria",
  zambia: "min_fare_zambia",
  tanzania: "min_fare_tanzania",
  "united republic of tanzania": "min_fare_tanzania",
  benin: "min_fare_benin",
  "burkina faso": "min_fare_burkina_faso",
  cameroon: "min_fare_cameroon",
  gabon: "min_fare_gabon",
  ghana: "min_fare_ghana",
  lesotho: "min_fare_lesotho",
  malawi: "min_fare_malawi",
  mozambique: "min_fare_mozambique",
  senegal: "min_fare_senegal",
  "sierra leone": "min_fare_sierra_leone",
};

const convenienceFeeMapping = {
  burundi: "convenience_fees_burundi",
  canada: "convenience_fees_canada",
  "democratic republic of the congo": "convenience_fees_drc",
  kenya: "convenience_fees_kenya",
  "south africa": "convenience_fees_rsa",
  rwanda: "convenience_fees_rwanda",
  uganda: "convenience_fees_uganda",
  nigeria: "convenience_fees_nigeria",
  zambia: "convenience_fees_zambia",
  tanzania: "convenience_fees_tanzania",
  "united republic of tanzania": "convenience_fees_tanzania",
  benin: "convenience_fees_benin",
  "burkina faso": "convenience_fees_burkina_faso",
  cameroon: "convenience_fees_cameroon",
  gabon: "convenience_fees_gabon",
  ghana: "convenience_fees_ghana",
  lesotho: "convenience_fees_lesotho",
  malawi: "convenience_fees_malawi",
  mozambique: "convenience_fees_mozambique",
  senegal: "convenience_fees_senegal",
  "sierra leone": "convenience_fees_sierra_leone",
};

/** Rounds a fare UP to the next multiple of `step` (e.g. 500 TZS), for cash. */
export const roundFareForCash = (amount, step) => {
  if (!step || step <= 0 || isNaN(amount)) return amount;
  return Math.ceil(amount / step) * step;
};

// LATRA compliance split for Tanzania trips.
export const TANZANIA_FEES = {
  LATRA_LEVY_RATE: 0.005,
  ADMIN_FEES_RATE: 0.105,
  DRIVER_SHARE_RATE: 0.89,
};

export const TANZANIA_ALLOWED_CAR_TYPES = ["economy", "comfort", "exclusive", "motorcycle", "bajaj"];

/**
 * Car types that have rates for this country (a per-distance rate or a
 * minimum fare); in Tanzania, only the licensed categories.
 */
export const getCountryCarTypes = (carTypes, country) => {
  if (!carTypes || !country) return carTypes || [];
  const key = normalizeCountry(country);
  const distanceKey = rateMapping[key];
  const minFareKey = minFareMapping[key];
  if (!distanceKey && !minFareKey) return carTypes;

  const priced = carTypes.filter(
    (car) =>
      (distanceKey && parseFloat(car[distanceKey] || 0) > 0) ||
      (minFareKey && parseFloat(car[minFareKey] || 0) > 0)
  );
  if (!isTanzaniaCountry(key)) return priced;
  return priced.filter((car) =>
    TANZANIA_ALLOWED_CAR_TYPES.includes((car.name || "").toLowerCase())
  );
};

/**
 * `options` (optional): { cashRounding, paymentMode }. Tanzania cash fares are
 * rounded up to `cashRounding`, never below the minimum fare.
 */
export const FareCalculator = (distance, time, rateDetails, instructionData, decimal, country, options) => {
    const cashRounding = (options && parseInt(options.cashRounding)) || 0;
    const paymentMode = (options && options.paymentMode) || "";

    const key = normalizeCountry(country);
    const ratePerUnitDistanceKey = rateMapping[key];
    const ratePerHourKey = ratePerHourMapping[key];
    const baseFareKey = baseFareMapping[key];
    const minFareKey = minFareMapping[key];

    const ratePerDistance = parseFloat(rateDetails[ratePerUnitDistanceKey]) || 0;
    const ratePerHour = parseFloat(rateDetails[ratePerHourKey]) || 0;
    const baseFare = parseFloat(rateDetails[baseFareKey]) || 0;
    const minFare = parseFloat(rateDetails[minFareKey]) || 0;

    // Missing rates used to produce NaN fares with no clue why.
    if (ratePerDistance === 0 && minFare === 0) {
        console.warn(
            `FareCalculator: No rates configured for country "${country}" on car type "${(rateDetails && rateDetails.name) || "unknown"}". Keys checked: ${ratePerUnitDistanceKey}, ${minFareKey}`
        );
    }

    let baseCalculated =
        ratePerDistance * parseFloat(distance) +
        ratePerHour * (parseFloat(time) / 3600);
    if (baseFare > 0) {
        baseCalculated += baseFare;
    }
    if (instructionData && instructionData.parcelTypeSelected) {
        baseCalculated += instructionData.parcelTypeSelected.amount;
    }
    if (instructionData && instructionData.optionSelected) {
        baseCalculated += instructionData.optionSelected.amount;
    }

    // Ensure total is at least the minimum fare
    const total = baseCalculated > minFare ? baseCalculated : minFare;

    // Convenience fee is always 0 - not charging for now
    const convenienceFee = 0;
    const grand = total + convenienceFee;
    const rawFare = parseFloat(grand.toFixed(decimal));

    let fare = grand;
    if (isTanzaniaCountry(country) && cashRounding > 0 && paymentMode === "cash") {
        const rounded = roundFareForCash(grand, cashRounding);
        fare = rounded < minFare ? minFare || rounded : rounded;
    }

    let latraLevy = 0;
    let adminFees = 0;
    let driverShare = fare;
    if (isTanzaniaCountry(country)) {
        latraLevy = Math.round(fare * TANZANIA_FEES.LATRA_LEVY_RATE);
        adminFees = Math.round(fare * TANZANIA_FEES.ADMIN_FEES_RATE);
        driverShare = fare - latraLevy - adminFees;
        if (driverShare < 0) driverShare = 0;
    }

    return {
        totalCost: parseFloat(total.toFixed(decimal)),
        grandTotal: parseFloat(fare.toFixed(decimal)),
        rawFare: rawFare,
        convenience_fees: parseFloat(convenienceFee.toFixed(decimal)),
        country: country,
        latra_levy: parseFloat(latraLevy.toFixed(decimal)),
        admin_fees: parseFloat(adminFees.toFixed(decimal)),
        driver_share: parseFloat(driverShare.toFixed(decimal)),
    };
};
